import React, { useState, useMemo, useEffect, useRef } from 'react'
import { NewAppLayout } from '@/components/new-ux/layout/NewAppLayout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useMasterInboxReplies, type MasterInboxReply } from '@/hooks/useMasterInboxReplies'
import { useDashboard } from '@/contexts/DashboardContext'
import { useClients } from '@/hooks/useClients'
import { useAuth } from '@/contexts/AuthContext'
import { useWorkspaceClient } from '@/hooks/useWorkspaceClient'
import { MasterInboxApiKeysTab } from '@/components/master-inbox/MasterInboxApiKeysTab'
import { MasterInboxLabelsTab } from '@/components/master-inbox/MasterInboxLabelsTab'
import { MasterInboxBlocklistTab } from '@/components/master-inbox/MasterInboxBlocklistTab'
import { MasterInboxLLMSettingsDialog } from '@/components/master-inbox/MasterInboxLLMSettingsDialog'
import { RefreshCw, Mail, Search, Calendar, User, Building, MessageSquare, ExternalLink, Filter, Bot, UserRound } from 'lucide-react'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { format } from 'date-fns'
import { TablePagination } from '@/components/ui/table-pagination'
import { MasterInboxReplyDetailsDialog } from '@/components/master-inbox/MasterInboxReplyDetailsDialog'
import { CadenceCell } from '@/components/master-inbox/CadenceCell'
import { useReplyCadenceBatch } from '@/hooks/useReplyCadenceBatch'
import { toast } from '@/hooks/useToastHistory'
import { supabase } from '@/integrations/supabase/client'

export default function MasterInbox() {
  const workspace = useWorkspaceClient()
  const replyFilter = workspace.isClientUser && workspace.loading ? null : workspace.workspaceName
  const { state } = useDashboard()
  const { data: clients = [] } = useClients()
  const { isAdmin } = useAuth()
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(25)
  const [activeTab, setActiveTab] = useState<'email' | 'linkedin' | 'api-keys' | 'labels' | 'blocklist'>('email')
  const [replyTypeFilter, setReplyTypeFilter] = useState<'all' | 'positive' | 'negative' | 'ooo_bounced' | 'ai_messup' | 'uncategorized'>('all')
  const [humanReplyFilter, setHumanReplyFilter] = useState<'all' | 'human' | 'auto'>('all')

  // Debounce search to avoid querying on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setCurrentPage(1)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Server-side filtered + paginated query
  const channelFilter = (activeTab === 'email' || activeTab === 'linkedin') ? activeTab : 'email'
  const { replies, loading, isRefreshing, error, count, kpis, refetch } = useMasterInboxReplies(replyFilter, {
    channel: channelFilter as 'email' | 'linkedin',
    replyType: replyTypeFilter,
    humanFilter: humanReplyFilter,
    search: debouncedSearch,
    page: currentPage,
    pageSize: itemsPerPage,
  })
  const [selectedReply, setSelectedReply] = useState<MasterInboxReply | null>(null)
  const [detailsOpen, setDetailsOpen] = useState(false)
  const [isGettingMessages, setIsGettingMessages] = useState(false)
  const [isSendingTestNotification, setIsSendingTestNotification] = useState(false)
  const [isRecategorizing, setIsRecategorizing] = useState(false)
  const [isSyncingToMasterInbox, setIsSyncingToMasterInbox] = useState(false)

  // Get selected client name from client ID
  const selectedClientName = useMemo(() => {
    if (state.selectedClient === 'all') return null
    const client = clients.find(c => c.id === state.selectedClient)
    return client?.name || null
  }, [state.selectedClient, clients])

  // Auto-refresh every minute
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)
  
  useEffect(() => {
    // Set up auto-refresh interval (every 60 seconds = 1 minute)
    refreshIntervalRef.current = setInterval(() => {
      refetch()
    }, 60000) // 60000ms = 1 minute

    // Cleanup interval on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
      }
    }
  }, [refetch])

  // KPIs come from the hook (server-side COUNT queries)
  // replies are already server-side filtered and paginated

  // Server-side pagination: replies are already the current page
  const filteredReplies = replies
  const totalPages = Math.ceil(count / itemsPerPage)
  const paginatedReplies = replies

  // Per-reply cadence metrics for the visible page. Only positive + human
  // rows return non-null metrics; everything else renders as em-dash.
  // Restricted to the email tab — LinkedIn threads use a different message
  // shape and the cadence math doesn't apply there yet.
  const cadenceEligibleIds = React.useMemo(() => {
    if (channelFilter !== 'email') return [] as string[]
    return paginatedReplies
      .filter(r => r.is_human_reply === true && [
        'Meeting Request','Meeting Booked','Interested',
        'Information Request','Pricing Inquiry','Specific Questions',
      ].includes(r.label_name ?? ''))
      .map(r => r.id)
  }, [paginatedReplies, channelFilter])
  const { data: cadenceMap } = useReplyCadenceBatch(cadenceEligibleIds)

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      // Try parsing the date string from masterinbox format
      return format(new Date(dateString), 'MMM d, yyyy h:mm a')
    } catch {
      return dateString
    }
  }

  const handleRowClick = async (reply: MasterInboxReply) => {
    try {
      // Fetch full reply data including JSONB columns for the dialog
      const { data: fullReply, error } = await supabase
        .from('masterinbox_replies')
        .select('*')
        .eq('id', reply.id)
        .single()

      if (error) {
        console.error('Error fetching full reply data:', error)
        // Still open dialog with partial data
        setSelectedReply(reply)
        setDetailsOpen(true)
      } else if (fullReply) {
        setSelectedReply(fullReply as MasterInboxReply)
        setDetailsOpen(true)
      }
    } catch (err) {
      console.error('Error in handleRowClick:', err)
      // Fallback: open with partial data
      setSelectedReply(reply)
      setDetailsOpen(true)
    }
  }

  const handleCloseDetails = () => {
    setDetailsOpen(false)
    setSelectedReply(null)
  }

  const handleViewInMasterInbox = (reply: MasterInboxReply) => {
    if (!reply.thread_url) {
      toast({
        title: 'No thread URL available',
        description: 'This reply does not have a MasterInbox thread_url set.',
        variant: 'destructive',
      })
      return
    }
    window.open(reply.thread_url, '_blank', 'noopener,noreferrer')
  }

  const handleGetMessages = async (reply: MasterInboxReply) => {
    if (!reply.prospect_id) {
      toast({
        title: 'Missing prospect_id',
        description: 'Cannot fetch messages without a prospect_id.',
        variant: 'destructive',
      })
      return
    }

    try {
      setIsGettingMessages(true)
      const { data, error } = await supabase.functions.invoke('masterinbox-refresh-single', {
        body: { replyId: reply.id },
      })

      if (error || data?.error) {
        const message = error?.message || data?.message || 'Failed to fetch messages from MasterInbox.'
        throw new Error(message)
      }

      toast({
        title: 'Messages refreshed',
        description: 'Latest MasterInbox thread data has been fetched for this reply.',
      })
      await refetch()
    } catch (err: any) {
      console.error('Error refreshing MasterInbox messages:', err)
      toast({
        title: 'Refresh failed',
        description: err?.message || 'Failed to refresh messages from MasterInbox.',
        variant: 'destructive',
      })
    } finally {
      setIsGettingMessages(false)
    }
  }

  const handleSendTestNotification = async (reply: MasterInboxReply) => {
    try {
      setIsSendingTestNotification(true)

      // Only allow test notifications for positive and negative replies
      const replyType = reply.reply_category_type
      if (!replyType || !['positive', 'negative'].includes(replyType)) {
        toast({
          title: 'Test Notification Not Available',
          description: 'Test notifications are only available for positive and negative replies.',
          variant: 'destructive',
        })
        return
      }

      // Determine notification type based on reply_category_type
      let notificationType: string
      let channelType: string

      if (replyType === 'positive') {
        notificationType = 'masterinboxPositiveReplyNotification'
        channelType = 'positive_replies'
      } else if (replyType === 'negative') {
        notificationType = 'masterinboxNegativeReplyNotification'
        channelType = 'neutral_negative_replies'
      } else {
        throw new Error(`Invalid reply type: ${replyType}`)
      }

      // Get client_id from workspace_name
      let clientId: string | null = null
      if (reply.workspace_name) {
        const client = clients.find(c => c.name === reply.workspace_name)
        if (client) {
          clientId = client.client_id?.toString() || client.id
        }
      }

      if (!clientId) {
        throw new Error('Could not determine client ID from workspace name')
      }

      console.log(`🧪 Sending MasterInbox test ${replyType} notification for reply ${reply.id} to test channel C0A79JSN4V8`)

      // Call scheduled-slack-reports with test channel override
      const { data, error } = await supabase.functions.invoke('scheduled-slack-reports', {
        body: {
          reportType: notificationType,
          clientId: clientId,
          channelType: channelType,
          dryRun: false,
          replyId: reply.id,
          testChannelId: 'C0A79JSN4V8', // Test channel for all MasterInbox notifications
          triggerSource: 'manual_test_notification'
        }
      })

      if (error) {
        throw error
      }

      if (data?.success) {
        toast({
          title: 'Test Notification Sent!',
          description: `${replyType.charAt(0).toUpperCase() + replyType.slice(1)} notification sent to test channel.`,
        })
      } else {
        throw new Error(data?.error || 'Notification failed')
      }
    } catch (error) {
      console.error('Test notification error:', error)
      toast({
        title: 'Test Notification Failed',
        description: error instanceof Error ? error.message : 'Failed to send test notification',
        variant: 'destructive',
      })
    } finally {
      setIsSendingTestNotification(false)
    }
  }

  const handleReCategorize = async (reply: MasterInboxReply) => {
    try {
      setIsRecategorizing(true)
      const { data, error } = await supabase.functions.invoke('categorize-masterinbox-reply', {
        body: { replyId: reply.id, force: true }, // Force manual categorization for testing
      })

      if (error) {
        // Try to extract error message from error object
        // Supabase may return error in different formats
        let errorMessage = 'Unknown error occurred'
        
        if (typeof error === 'string') {
          errorMessage = error
        } else if (error?.message) {
          errorMessage = error.message
        } else if (error?.error) {
          errorMessage = error.error
        } else if (typeof error === 'object') {
          // Try to stringify the error object
          try {
            errorMessage = JSON.stringify(error)
          } catch {
            errorMessage = String(error)
          }
        }
        
        console.error('Edge Function error:', error)
        throw new Error(errorMessage)
      }

      if (!data?.success) {
        // Extract error message from response data
        const errorMessage = data?.error || data?.message || data?.reason || 'Failed to categorize reply.'
        console.error('Categorization failed:', data)
        
        // Provide helpful messages for common issues
        if (data?.reason === 'no_workspace') {
          throw new Error('Reply has no workspace_name. Cannot categorize.')
        } else if (data?.reason === 'client_not_found') {
          throw new Error(`Client not found for workspace: ${reply.workspace_name || 'Unknown'}`)
        } else if (data?.reason === 'no_api_key') {
          throw new Error('Perplexity API key not configured for this client. Please configure it in API Keys settings.')
        } else if (data?.reason === 'categorization_disabled') {
          throw new Error('AI categorization is disabled for this client.')
        }
        
        throw new Error(errorMessage)
      }

      toast({
        title: 'Success',
        description: `Reply categorized as "${data.categorization?.label_name || 'Unknown'}". Use "Sync to MasterInbox" button to sync when ready.`,
      })

      // Refresh to show updated categorization
      await refetch()
      
      // Update the selected reply in the dialog with the new categorization data
      if (selectedReply && selectedReply.id === reply.id) {
        const { data: updatedReply, error: fetchError } = await supabase
          .from('masterinbox_replies')
          .select('*')
          .eq('id', reply.id)
          .single()
        
        if (!fetchError && updatedReply) {
          setSelectedReply(updatedReply as MasterInboxReply)
        }
      }
    } catch (err: any) {
      console.error('Error recategorizing:', err)
      const errorMessage = err.message || err.toString() || 'Failed to recategorize reply.'
      toast({
        title: 'Categorization failed',
        description: errorMessage,
        variant: 'destructive',
      })
    } finally {
      setIsRecategorizing(false)
    }
  }

  const handleSyncToMasterInbox = async (reply: MasterInboxReply) => {
    if (!reply.prospect_id) {
      toast({
        title: 'Missing prospect_id',
        description: 'Cannot sync to MasterInbox without a prospect_id.',
        variant: 'destructive',
      })
      return
    }

    if (!reply.label_id) {
      toast({
        title: 'Not categorized',
        description: 'Reply must be categorized before syncing to MasterInbox.',
        variant: 'destructive',
      })
      return
    }

    try {
      setIsSyncingToMasterInbox(true)
      const { data, error } = await supabase.functions.invoke('sync-masterinbox-label', {
        body: { replyId: reply.id },
      })

      if (error || !data?.success) {
        const message = error?.message || data?.error || 'Failed to sync label to MasterInbox.'
        throw new Error(message)
      }

      toast({
        title: 'Success',
        description: `"${reply.label_name}" synced to list successfully`,
      })

      // Refresh to show updated sync status
      await refetch()
    } catch (err: any) {
      console.error('Error syncing to MasterInbox:', err)
      toast({
        title: 'Sync failed',
        description: err.message || 'Failed to sync to list.',
        variant: 'destructive',
      })
    } finally {
      setIsSyncingToMasterInbox(false)
    }
  }

  return (
    <NewAppLayout fullWidth>
      <div className="h-full flex flex-col space-y-4">
              {/* Page Header */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl font-semibold">Replies</h1>
                  {isAdmin && (
                    <p className="text-muted-foreground mt-1">
                      View and manage replies from all sequencers
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                  <div className="text-sm text-muted-foreground whitespace-nowrap">
                    Total Replies: <span className="font-semibold">{kpis.totalAll}</span>
                  </div>
                  <Button 
                    onClick={() => refetch()} 
                    disabled={loading || isRefreshing}
                    variant="outline"
                    size="sm"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                  {isAdmin && <MasterInboxLLMSettingsDialog />}
                </div>
              </div>

              {/* Statistics Cards — server-side counts */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-shrink-0">
                <Card>
                  <CardHeader className="flex flex-col items-start space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Today's Replies</CardTitle>
                    <div className="text-2xl font-bold mt-2">{kpis.todayTotal}</div>
                    <div className="text-xs text-muted-foreground mt-1">Email + LinkedIn</div>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="flex flex-col items-start space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Total Replies</CardTitle>
                    <div className="text-2xl font-bold mt-2">{kpis.totalAll}</div>
                    <div className="text-xs text-muted-foreground mt-1">Email + LinkedIn</div>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="flex flex-col items-start space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">Email Replies</CardTitle>
                    <div className="text-2xl font-bold mt-2">{kpis.emailCount}</div>
                    <div className="text-xs text-muted-foreground mt-1">Out of {kpis.totalAll} total</div>
                  </CardHeader>
                </Card>
                <Card>
                  <CardHeader className="flex flex-col items-start space-y-0 pb-2">
                    <CardTitle className="text-sm font-medium">LinkedIn Replies</CardTitle>
                    <div className="text-2xl font-bold mt-2">{kpis.linkedinCount}</div>
                    <div className="text-xs text-muted-foreground mt-1">Out of {kpis.totalAll} total</div>
                  </CardHeader>
                </Card>
              </div>

              {/* Pagination Info */}
              <div className="text-sm text-muted-foreground px-1">
                Showing {count > 0 ? Math.min((currentPage - 1) * itemsPerPage + 1, count) : 0} to {Math.min(currentPage * itemsPerPage, count)} of {count} results
              </div>

              {/* Tabs for Email vs LinkedIn Outreach */}
              <Tabs value={activeTab} onValueChange={(value) => {
                setActiveTab(value as 'email' | 'linkedin' | 'api-keys' | 'labels' | 'blocklist')
                setCurrentPage(1) // Reset to first page when switching tabs
              }} className="w-full">
                <div className="border-b mb-4">
                  <TabsList className={`bg-transparent border-0 p-0 h-auto w-full grid ${isAdmin ? 'grid-cols-5' : 'grid-cols-2'}`}>
                    <TabsTrigger
                      value="email"
                      className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none w-full"
                    >
                      Email Outreach
                    </TabsTrigger>
                    <TabsTrigger
                      value="linkedin"
                      className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none w-full"
                    >
                      LinkedIn Outreach
                    </TabsTrigger>
                    {isAdmin && (
                      <TabsTrigger
                        value="api-keys"
                        className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none w-full"
                      >
                        API Keys
                      </TabsTrigger>
                    )}
                    {isAdmin && (
                      <TabsTrigger
                        value="labels"
                        className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none w-full"
                      >
                        Labels
                      </TabsTrigger>
                    )}
                    {isAdmin && (
                      <TabsTrigger
                        value="blocklist"
                        className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none w-full"
                      >
                        Blocklist
                      </TabsTrigger>
                    )}
                  </TabsList>
                </div>

                {/* Email Outreach Tab */}
                <TabsContent value="email" className="mt-0">
                  <Card className="flex-1 flex flex-col min-h-0">
                    <CardHeader className="flex-shrink-0">
                      <CardTitle>Master Inbox Replies</CardTitle>
                      <CardDescription className="mb-0">
                        Email replies from Smartlead, Instantly, Email Bison sequencers
                      </CardDescription>

                  {/* Search and Filters */}
                  <div className="flex items-center gap-4 pt-4">
                    <div className="flex items-center gap-2 flex-1">
                      <Search className="h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by email, name, subject, or campaign..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value)
                          setCurrentPage(1) // Reset to first page on search
                        }}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={replyTypeFilter} onValueChange={(value) => {
                        setReplyTypeFilter(value as typeof replyTypeFilter)
                        setCurrentPage(1)
                      }}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Reply Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Reply Types</SelectItem>
                          <SelectItem value="positive">Positive</SelectItem>
                          <SelectItem value="negative">Negative</SelectItem>
                          <SelectItem value="ooo_bounced">OOO/Bounced</SelectItem>
                          <SelectItem value="ai_messup">AI Messup</SelectItem>
                          <SelectItem value="uncategorized">Uncategorized</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={humanReplyFilter} onValueChange={(value) => {
                        setHumanReplyFilter(value as typeof humanReplyFilter)
                        setCurrentPage(1)
                      }}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Reply Origin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Replies</SelectItem>
                          <SelectItem value="human">Human Only</SelectItem>
                          <SelectItem value="auto">Auto Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value))
                          setCurrentPage(1)
                        }}
                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col min-h-0">
                  {error && (
                    <div className="text-destructive text-sm mb-4">{error}</div>
                  )}

                  {/* Show loading spinner only on initial load, not during background refresh */}
                  {loading && replies.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredReplies.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                      <Mail className="w-12 h-12 mb-4 opacity-50" />
                      <p className="text-lg font-medium">No replies found</p>
                      <p className="text-sm">
                        {searchTerm ? 'Try adjusting your search terms' : 'Replies will appear here when received via webhook'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 w-full overflow-hidden">
                        <div className="w-full overflow-x-hidden">
                          <Table className="w-full table-fixed">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[8%]">Date</TableHead>
                                <TableHead className="w-[9%]">Lead/Prospect</TableHead>
                                <TableHead className="w-[9%]">Sender</TableHead>
                                <TableHead className="w-[14%]">Reply</TableHead>
                                <TableHead className="w-[9%]">Campaign</TableHead>
                                <TableHead className="w-[7%]">Workspace</TableHead>
                                <TableHead className="w-[5%]">Reply #</TableHead>
                                <TableHead className="w-[10%]">Category/Reply-Type</TableHead>
                                <TableHead className="w-[6%]">Origin</TableHead>
                                <TableHead className="w-[12%]">Cadence</TableHead>
                                <TableHead className="w-[5%]">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedReplies.map((reply) => {
                                return (
                                  <TableRow
                                    key={reply.id}
                                    className="cursor-pointer hover:bg-muted/40"
                                    onClick={() => handleRowClick(reply)}
                                  >
                                    <TableCell className="w-[7%]">
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        <span className="text-sm break-words">
                                          {formatDate(reply.created_at)}
                                        </span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[9%]">
                                      <div className="flex flex-col">
                                        {/* Lead/Prospect: from_name/from_address for both email and LinkedIn */}
                                        <span className="font-medium break-words">{reply.from_name || reply.name || ''}</span>
                                        {reply.channel_id?.toLowerCase().includes('heyreach') && reply.linkedin ? (
                                          <a
                                            href={reply.linkedin.startsWith('http') ? reply.linkedin : `https://www.linkedin.com/in/${reply.linkedin}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-muted-foreground break-words hover:underline"
                                          >
                                            {reply.linkedin}
                                          </a>
                                        ) : (
                                          <span className="text-xs text-muted-foreground break-words">{reply.from_address || reply.email || reply.channel || ''}</span>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[9%]">
                                      <div className="flex flex-col">
                                        {/* Sender: to_name/to_address for both email and LinkedIn */}
                                        <span className="font-medium break-words">{reply.to_name || ''}</span>
                                        <span className="text-xs text-muted-foreground break-words">{reply.to_address || reply.channel || ''}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[14%]">
                                      <div className="max-h-[100px] overflow-y-auto break-words pr-2">
                                        {reply.text || ''}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[9%]">
                                      <div className="flex flex-col">
                                        {reply.campaign_name && (
                                          <div className="break-words font-medium">
                                            {reply.campaign_name}
                                          </div>
                                        )}
                                        {reply.campaign_id && (
                                          <div className="text-xs text-muted-foreground">
                                            ID: {reply.campaign_id}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[7%]">
                                      <div className="flex flex-col">
                                        {reply.workspace_name && (
                                          <Badge variant="outline" className="w-fit break-words">{reply.workspace_name}</Badge>
                                        )}
                                        {reply.workspace_id && (
                                          <div className="text-xs text-muted-foreground mt-1">
                                            ID: {reply.workspace_id}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[5%]">
                                      {reply.reply_count != null ? (
                                        <Badge variant="outline" className={`w-fit text-xs ${
                                          reply.reply_count === 1 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                          reply.reply_count === 2 ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                          'bg-orange-50 text-orange-700 border-orange-200'
                                        }`}>
                                          {reply.reply_count === 1 ? '1st' :
                                           reply.reply_count === 2 ? '2nd' :
                                           `${reply.reply_count}th`}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="w-[10%]">
                                      <div className="flex flex-col gap-1">
                                        {reply.label_name ? (
                                          <Badge variant="outline" className="break-words w-fit">
                                            {reply.label_name}
                                          </Badge>
                                        ) : (
                                          <span className="text-xs text-muted-foreground italic">Not Categorized</span>
                                        )}
                                        {reply.reply_category_type && (() => {
                                          const normalizedType = reply.reply_category_type.toLowerCase()
                                          let colorClass = ''

                                          if (normalizedType === 'positive') {
                                            colorClass = 'bg-green-100 text-green-800 border-green-300'
                                          } else if (normalizedType === 'negative') {
                                            colorClass = 'bg-red-100 text-red-800 border-red-300'
                                          } else if (normalizedType === 'ooo_bounced' || normalizedType === 'ooo' || normalizedType === 'auto-reply' || normalizedType === 'autoreply' || normalizedType === 'out of office') {
                                            colorClass = 'bg-purple-100 text-purple-800 border-purple-300'
                                          } else if (normalizedType === 'ai_messup' || normalizedType === 'neutral') {
                                            colorClass = 'bg-orange-100 text-orange-800 border-orange-300'
                                          }

                                          return (
                                            <Badge
                                              variant="outline"
                                              className={`w-fit text-xs ${colorClass}`}
                                            >
                                              {reply.reply_category_type}
                                            </Badge>
                                          )
                                        })()}
                                        {reply.sub_label && (
                                          <Badge variant="outline" className="w-fit text-xs bg-blue-50 text-blue-700 border-blue-200">
                                            {reply.sub_label}
                                          </Badge>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[6%]">
                                      {reply.is_human_reply !== null && reply.is_human_reply !== undefined ? (
                                        <Badge variant="outline" className={`w-fit text-xs ${reply.is_human_reply ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                          {reply.is_human_reply ? <UserRound className="h-3 w-3 mr-1" /> : <Bot className="h-3 w-3 mr-1" />}
                                          {reply.is_human_reply ? 'Human' : 'Auto'}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="w-[12%]" onClick={(e) => e.stopPropagation()}>
                                      <CadenceCell metrics={cadenceMap?.get(reply.id) ?? null} />
                                    </TableCell>
                                    <TableCell className="w-[5%]">
                                      {reply.thread_url && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            window.open(reply.thread_url, '_blank')
                                          }}
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      {/* Pagination */}
                      <div className="mt-4 border-t pt-4">
                        <TablePagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          itemsPerPage={itemsPerPage}
                          totalItems={filteredReplies.length}
                          onPageChange={setCurrentPage}
                          onItemsPerPageChange={setItemsPerPage}
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              </TabsContent>

              {/* LinkedIn Outreach Tab */}
              <TabsContent value="linkedin" className="mt-0">
                <Card className="flex-1 flex flex-col min-h-0">
                  <CardHeader className="flex-shrink-0">
                    <CardTitle>Master Inbox Replies</CardTitle>
                    <CardDescription>
                      LinkedIn replies from HeyReach sequencer
                    </CardDescription>

                  {/* Search and Filters */}
                  <div className="flex items-center gap-4 pt-4">
                    <div className="flex items-center gap-2 flex-1">
                      <Search className="h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by email, name, subject, or campaign..."
                        value={searchTerm}
                        onChange={(e) => {
                          setSearchTerm(e.target.value)
                          setCurrentPage(1)
                        }}
                        className="flex-1"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={replyTypeFilter} onValueChange={(value) => {
                        setReplyTypeFilter(value as typeof replyTypeFilter)
                        setCurrentPage(1)
                      }}>
                        <SelectTrigger className="w-[180px]">
                          <SelectValue placeholder="Reply Type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Reply Types</SelectItem>
                          <SelectItem value="positive">Positive</SelectItem>
                          <SelectItem value="negative">Negative</SelectItem>
                          <SelectItem value="ooo_bounced">OOO/Bounced</SelectItem>
                          <SelectItem value="ai_messup">AI Messup</SelectItem>
                          <SelectItem value="uncategorized">Uncategorized</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select value={humanReplyFilter} onValueChange={(value) => {
                        setHumanReplyFilter(value as typeof humanReplyFilter)
                        setCurrentPage(1)
                      }}>
                        <SelectTrigger className="w-[160px]">
                          <SelectValue placeholder="Reply Origin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Replies</SelectItem>
                          <SelectItem value="human">Human Only</SelectItem>
                          <SelectItem value="auto">Auto Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">Rows per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value))
                          setCurrentPage(1)
                        }}
                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="flex-1 flex flex-col min-h-0">
                  {error && (
                    <div className="text-destructive text-sm mb-4">{error}</div>
                  )}

                  {/* Show loading spinner only on initial load, not during background refresh */}
                  {loading && replies.length === 0 ? (
                    <div className="flex items-center justify-center h-64">
                      <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : filteredReplies.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
                      <Mail className="w-12 h-12 mb-4 opacity-50" />
                      <p className="text-lg font-medium">No replies found</p>
                      <p className="text-sm">
                        {searchTerm ? 'Try adjusting your search terms' : 'Replies will appear here when received via webhook'}
                      </p>
                    </div>
                  ) : (
                    <>
                      <div className="flex-1 w-full overflow-hidden">
                        <div className="w-full overflow-x-hidden">
                          <Table className="w-full table-fixed">
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-[8%]">Date</TableHead>
                                <TableHead className="w-[10%]">Lead/Prospect</TableHead>
                                <TableHead className="w-[10%]">Sender</TableHead>
                                <TableHead className="w-[18%]">Reply</TableHead>
                                <TableHead className="w-[10%]">Campaign</TableHead>
                                <TableHead className="w-[8%]">Workspace</TableHead>
                                <TableHead className="w-[5%]">Reply #</TableHead>
                                <TableHead className="w-[12%]">Category/Reply-Type</TableHead>
                                <TableHead className="w-[7%]">Origin</TableHead>
                                <TableHead className="w-[5%]">Actions</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {paginatedReplies.map((reply) => {
                                return (
                                  <TableRow
                                    key={reply.id}
                                    className="cursor-pointer hover:bg-muted/40"
                                    onClick={() => handleRowClick(reply)}
                                  >
                                    <TableCell className="w-[8%]">
                                      <div className="flex items-center gap-2">
                                        <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                                        <span className="text-sm break-words">
                                          {formatDate(reply.created_at)}
                                        </span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[10%]">
                                      <div className="flex flex-col">
                                        {/* Lead/Prospect: from_name/from_address for both email and LinkedIn */}
                                        <span className="font-medium break-words">{reply.from_name || reply.name || ''}</span>
                                        {reply.channel_id?.toLowerCase().includes('heyreach') && reply.linkedin ? (
                                          <a
                                            href={reply.linkedin.startsWith('http') ? reply.linkedin : `https://www.linkedin.com/in/${reply.linkedin}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-muted-foreground break-words hover:underline"
                                          >
                                            {reply.linkedin}
                                          </a>
                                        ) : (
                                          <span className="text-xs text-muted-foreground break-words">{reply.from_address || reply.email || reply.channel || ''}</span>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[10%]">
                                      <div className="flex flex-col">
                                        {/* Sender: to_name/to_address for both email and LinkedIn */}
                                        <span className="font-medium break-words">{reply.to_name || ''}</span>
                                        <span className="text-xs text-muted-foreground break-words">{reply.to_address || reply.channel || ''}</span>
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[18%]">
                                      <div className="max-h-[100px] overflow-y-auto break-words pr-2">
                                        {reply.text || ''}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[10%]">
                                      <div className="flex flex-col">
                                        {reply.campaign_name && (
                                          <div className="break-words font-medium">
                                            {reply.campaign_name}
                                          </div>
                                        )}
                                        {reply.campaign_id && (
                                          <div className="text-xs text-muted-foreground">
                                            ID: {reply.campaign_id}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[8%]">
                                      <div className="flex flex-col">
                                        {reply.workspace_name && (
                                          <Badge variant="outline" className="w-fit break-words">{reply.workspace_name}</Badge>
                                        )}
                                        {reply.workspace_id && (
                                          <div className="text-xs text-muted-foreground mt-1">
                                            ID: {reply.workspace_id}
                                          </div>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[5%]">
                                      {reply.reply_count != null ? (
                                        <Badge variant="outline" className={`w-fit text-xs ${
                                          reply.reply_count === 1 ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                          reply.reply_count === 2 ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                          'bg-orange-50 text-orange-700 border-orange-200'
                                        }`}>
                                          {reply.reply_count === 1 ? '1st' :
                                           reply.reply_count === 2 ? '2nd' :
                                           `${reply.reply_count}th`}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="w-[12%]">
                                      <div className="flex flex-col gap-1">
                                        {reply.label_name ? (
                                          <Badge variant="outline" className="break-words w-fit">
                                            {reply.label_name}
                                          </Badge>
                                        ) : (
                                          <span className="text-xs text-muted-foreground italic">Not Categorized</span>
                                        )}
                                        {reply.reply_category_type && (() => {
                                          const normalizedType = reply.reply_category_type.toLowerCase()
                                          let colorClass = ''

                                          if (normalizedType === 'positive') {
                                            colorClass = 'bg-green-100 text-green-800 border-green-300'
                                          } else if (normalizedType === 'negative') {
                                            colorClass = 'bg-red-100 text-red-800 border-red-300'
                                          } else if (normalizedType === 'ooo_bounced' || normalizedType === 'ooo' || normalizedType === 'auto-reply' || normalizedType === 'autoreply' || normalizedType === 'out of office') {
                                            colorClass = 'bg-purple-100 text-purple-800 border-purple-300'
                                          } else if (normalizedType === 'ai_messup' || normalizedType === 'neutral') {
                                            colorClass = 'bg-orange-100 text-orange-800 border-orange-300'
                                          }

                                          return (
                                            <Badge
                                              variant="outline"
                                              className={`w-fit text-xs ${colorClass}`}
                                            >
                                              {reply.reply_category_type}
                                            </Badge>
                                          )
                                        })()}
                                        {reply.sub_label && (
                                          <Badge variant="outline" className="w-fit text-xs bg-blue-50 text-blue-700 border-blue-200">
                                            {reply.sub_label}
                                          </Badge>
                                        )}
                                      </div>
                                    </TableCell>
                                    <TableCell className="w-[7%]">
                                      {reply.is_human_reply !== null && reply.is_human_reply !== undefined ? (
                                        <Badge variant="outline" className={`w-fit text-xs ${reply.is_human_reply ? 'bg-sky-50 text-sky-700 border-sky-200' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                          {reply.is_human_reply ? <UserRound className="h-3 w-3 mr-1" /> : <Bot className="h-3 w-3 mr-1" />}
                                          {reply.is_human_reply ? 'Human' : 'Auto'}
                                        </Badge>
                                      ) : (
                                        <span className="text-xs text-muted-foreground">-</span>
                                      )}
                                    </TableCell>
                                    <TableCell className="w-[5%]">
                                      {reply.thread_url && (
                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={(e) => {
                                            e.stopPropagation()
                                            window.open(reply.thread_url, '_blank')
                                          }}
                                        >
                                          <ExternalLink className="w-4 h-4" />
                                        </Button>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </div>
                      </div>

                      {/* Pagination */}
                      <div className="mt-4 border-t pt-4">
                        <TablePagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          itemsPerPage={itemsPerPage}
                          totalItems={filteredReplies.length}
                          onPageChange={setCurrentPage}
                          onItemsPerPageChange={setItemsPerPage}
                        />
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
              </TabsContent>

              {/* API Keys Tab (Admin Only) */}
              {isAdmin && (
                <TabsContent value="api-keys" className="mt-0">
                  <MasterInboxApiKeysTab />
                </TabsContent>
              )}
              {/* Labels Tab (Admin Only) */}
              {isAdmin && (
                <TabsContent value="labels" className="mt-0">
                  <MasterInboxLabelsTab />
                </TabsContent>
              )}
              {/* Blocklist Tab (Admin Only) */}
              {isAdmin && (
                <TabsContent value="blocklist" className="mt-0">
                  <MasterInboxBlocklistTab />
                </TabsContent>
              )}
              </Tabs>
              {selectedReply && (
                <MasterInboxReplyDetailsDialog
                  reply={selectedReply}
                  isOpen={detailsOpen}
                  isAdmin={isAdmin}
                  onClose={handleCloseDetails}
                  onRefresh={refetch}
                  onViewInMasterInbox={isAdmin ? handleViewInMasterInbox : undefined}
                  onGetMessages={isAdmin ? handleGetMessages : undefined}
                  onReCategorize={isAdmin ? handleReCategorize : undefined}
                  onSyncToMasterInbox={isAdmin ? handleSyncToMasterInbox : undefined}
                  onSendTestNotification={isAdmin ? handleSendTestNotification : undefined}
                  isGettingMessages={isGettingMessages}
                  isSendingTestNotification={isSendingTestNotification}
                  isRecategorizing={isRecategorizing}
                  isSyncingToMasterInbox={isSyncingToMasterInbox}
                />
              )}
      </div>
    </NewAppLayout>
  )
}

